import { useEffect, useRef, useState } from "react";
import {
  CalendarCheck2,
  ChevronDown,
  Copy,
  Download,
  ImageIcon,
  MapPin,
  RefreshCw,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import {
  ActionButton,
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
  Panel,
  SectionHeader,
  Tag,
} from "../components/design-system";
import { useResource } from "../hooks/use-resource";
import {
  groupRecognizedPlans,
  planTime,
  recognitionDateRange,
  recognitionStatus,
  recognitionText,
  type RecognitionDetail,
  type RecognitionFilter,
  type RecognitionJob,
  type RecognitionUser,
} from "../schedule-recognition";
import { formatDateTime } from "../utils";
import "./ai-schedule.css";

const FILTERS = [
  { value: "", label: "全部" },
  { value: "active", label: "识别中" },
  { value: "completed", label: "已添加" },
  { value: "failed", label: "未完成" },
] as const;

export function AiSchedulePage() {
  const [filter, setFilter] = useState<RecognitionFilter>({});
  const [query, setQuery] = useState("");
  const [from, setFrom] = useState("");
  const [through, setThrough] = useState("");
  return (
    <div className="ai-schedule space-y-6">
      <SectionHeader
        eyebrow="AI SCHEDULE"
        title="AI日程"
        desc="按用户查看提交内容与识别结果"
      />
      <form
        className="ai-schedule-filters"
        onSubmit={(event) => {
          event.preventDefault();
          try {
            setFilter({
              ...filter,
              q: query.trim() || undefined,
              ...recognitionDateRange(from, through),
            });
          } catch (error) {
            toast.error((error as Error).message);
          }
        }}
      >
        <label className="ai-schedule-search">
          <Search size={16} />
          <input
            aria-label="搜索用户、原文或日程"
            placeholder="搜索姓名、学号、原文或日程"
            maxLength={100}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <label className="ai-schedule-date">
          提交日期
          <input
            type="date"
            aria-label="提交开始日期"
            value={from}
            max={through || undefined}
            onChange={(event) => setFrom(event.target.value)}
          />
        </label>
        <label className="ai-schedule-date">
          至
          <input
            type="date"
            aria-label="提交结束日期"
            value={through}
            min={from || undefined}
            onChange={(event) => setThrough(event.target.value)}
          />
        </label>
        <ActionButton type="submit" tone="dark">
          筛选
        </ActionButton>
        {(filter.q || filter.from || filter.to || filter.status) && (
          <ActionButton
            type="button"
            onClick={() => {
              setQuery("");
              setFrom("");
              setThrough("");
              setFilter({});
            }}
          >
            重置
          </ActionButton>
        )}
      </form>
      <div className="flex flex-wrap gap-2" aria-label="识别状态">
        {FILTERS.map((item) => (
          <button
            type="button"
            key={item.value}
            aria-pressed={(filter.status || "") === item.value}
            className={`ai-schedule-filter ${(filter.status || "") === item.value ? "is-selected" : ""}`}
            onClick={() =>
              setFilter({ ...filter, status: item.value || undefined })
            }
          >
            {item.label}
          </button>
        ))}
      </div>
      <RecognitionBrowser key={JSON.stringify(filter)} filter={filter} />
    </div>
  );
}

function RecognitionBrowser({ filter }: { filter: RecognitionFilter }) {
  const [userSearch, setUserSearch] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [sort, setSort] = useState<"latest" | "submissions">("latest");
  const [page, setPage] = useState(1),
    [selectedId, setSelectedId] = useState<string | null>(null);
  const [polling, setPolling] = useState(true),
    [revision, setRevision] = useState(0);
  const users = useResource(
    () =>
      api.recognitionUsers({
        ...filter,
        userQuery: userQuery || undefined,
        sort,
        page,
        pageSize: 12,
      }),
    [filter, userQuery, sort, page],
    polling ? 10000 : 0,
  );
  const selected =
    users.data?.items.find((item) => item.id === selectedId) ||
    users.data?.items[0];
  const summary = users.data?.summary;
  return (
    <>
      <div className="ai-schedule-summary">
        {(
          [
            ["提交用户", summary?.users],
            ["识别任务", summary?.total],
            ["识别中", summary?.active],
            ["已添加", summary?.completed],
            ["未完成", summary?.failed],
          ] as const
        ).map(([label, count]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>
              {count === undefined ? "—" : count.toLocaleString()}
            </strong>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-ink-faint">
          {users.data
            ? `${users.data.pagination.total} 位匹配用户`
            : "用户记录"}
        </p>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-ink-soft">
            <input
              type="checkbox"
              checked={polling}
              onChange={(event) => setPolling(event.target.checked)}
            />
            自动刷新
          </label>
          <ActionButton
            busy={users.refreshing}
            onClick={() => {
              void users.refresh();
              setRevision((value) => value + 1);
            }}
          >
            <RefreshCw size={14} />
            刷新
          </ActionButton>
        </div>
      </div>
      {users.error && (
        <ErrorState error={users.error} onRetry={() => void users.refresh()} />
      )}
      <div className="ai-schedule-workspace">
        <Panel className="ai-schedule-users">
          <div className="ai-schedule-panel-heading">
            <h3>用户</h3>
            <span>成功 / 总提交</span>
          </div>
          <form
            className="ai-schedule-user-filters"
            onSubmit={(event) => {
              event.preventDefault();
              setPage(1);
              setSelectedId(null);
              setUserQuery(userSearch.trim());
            }}
          >
            <label className="ai-schedule-search">
              <Search size={14} />
              <input
                aria-label="搜索提交用户"
                placeholder="姓名或学号"
                maxLength={100}
                value={userSearch}
                onChange={(event) => setUserSearch(event.target.value)}
              />
              <button type="submit" aria-label="搜索用户">
                <Search size={14} />
              </button>
              {userSearch && (
                <button
                  type="button"
                  aria-label="清空用户搜索"
                  onClick={() => {
                    setUserSearch("");
                    setUserQuery("");
                    setPage(1);
                    setSelectedId(null);
                  }}
                >
                  <X size={14} />
                </button>
              )}
            </label>
            <select
              aria-label="用户排序"
              value={sort}
              onChange={(event) => {
                setSort(event.target.value as "latest" | "submissions");
                setPage(1);
                setSelectedId(null);
              }}
            >
              <option value="latest">最近提交优先</option>
              <option value="submissions">提交次数从多到少</option>
            </select>
          </form>
          {users.loading && !users.data ? (
            <LoadingState rows={5} />
          ) : users.data?.items.length ? (
            <>
              <div className="ai-schedule-user-list" aria-label="提交用户列表">
                {users.data.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`ai-schedule-user ${selected?.id === item.id ? "is-selected" : ""}`}
                    aria-pressed={selected?.id === item.id}
                    onClick={() => setSelectedId(item.id)}
                  >
                    <span className="ai-schedule-avatar" aria-hidden="true">
                      {Array.from(item.name || item.account)[0]}
                    </span>
                    <span className="min-w-0 flex-1">
                      <strong>
                        {item.name || item.account}
                        {!item.enabled && (
                          <span className="ai-schedule-disabled">已禁用</span>
                        )}
                      </strong>
                      <span className="ai-schedule-account">
                        {item.account}
                      </span>
                      <span className="ai-schedule-user-meta">
                        {item.successfulJobs}/{item.totalJobs} ·{" "}
                        {formatDateTime(item.latestAt, true)}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
              <Pagination
                page={page}
                pages={users.data.pagination.totalPages}
                total={users.data.pagination.total}
                onChange={(value) => {
                  setPage(value);
                  setSelectedId(null);
                }}
                className="border-t border-sand-light p-3"
              />
            </>
          ) : (
            <EmptyState
              title="没有匹配用户"
              description="调整筛选条件后再试。"
            />
          )}
        </Panel>
        {selected ? (
          <UserRecognitions
            key={selected.id}
            user={selected}
            filter={filter}
            polling={polling}
            revision={revision}
            onAccessChanged={(enabled) => {
              users.setData((current) =>
                current
                  ? {
                      ...current,
                      items: current.items.map((item) =>
                        item.id === selected.id ? { ...item, enabled } : item,
                      ),
                    }
                  : current,
              );
              void users.refresh();
            }}
          />
        ) : (
          <Panel>
            <EmptyState
              title="选择一位用户"
              description="原始提交与识别结果会显示在这里。"
            />
          </Panel>
        )}
      </div>
    </>
  );
}

function UserRecognitions({
  user,
  filter,
  polling,
  revision,
  onAccessChanged,
}: {
  user: RecognitionUser;
  filter: RecognitionFilter;
  polling: boolean;
  revision: number;
  onAccessChanged: (enabled: boolean) => void;
}) {
  const [accessBusy, setAccessBusy] = useState(false);
  const [page, setPage] = useState(1),
    [expanded, setExpanded] = useState<string | null>(null);
  const jobs = useResource(
    () => api.recognitionJobs(user.id, { ...filter, page, pageSize: 10 }),
    [user.id, filter, page, revision],
    polling ? 10000 : 0,
  );
  return (
    <Panel className="min-w-0 overflow-hidden">
      <div className="ai-schedule-panel-heading">
        <div>
          <h3>{user.name || user.account}</h3>
          <span>
            {user.account} · {jobs.data?.pagination.total ?? user.jobs}{" "}
            条识别记录
          </span>
        </div>
        <ActionButton
          busy={accessBusy}
          tone={user.enabled ? "danger" : "light"}
          onClick={async () => {
            setAccessBusy(true);
            try {
              const result = await api.setRecognitionAccess(
                user.id,
                !user.enabled,
              );
              onAccessChanged(result.enabled);
              toast.success(result.enabled ? "已恢复使用" : "已禁用 AI 日程");
            } catch (error) {
              toast.error((error as Error).message);
            } finally {
              setAccessBusy(false);
            }
          }}
        >
          {user.enabled ? "禁用 AI 日程" : "恢复使用"}
        </ActionButton>
      </div>
      {jobs.error && (
        <ErrorState error={jobs.error} onRetry={() => void jobs.refresh()} />
      )}
      {jobs.loading && !jobs.data ? (
        <LoadingState rows={6} />
      ) : jobs.data?.items.length ? (
        <>
          <div className="divide-y divide-sand-light/70">
            {jobs.data.items.map((job) => (
              <div key={job.id}>
                <button
                  type="button"
                  className="ai-schedule-job-heading"
                  aria-expanded={expanded === job.id}
                  aria-controls={`recognition-${job.id}`}
                  onClick={() =>
                    setExpanded(expanded === job.id ? null : job.id)
                  }
                >
                  <span className="ai-schedule-job-icon">
                    <CalendarCheck2 size={19} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="ai-schedule-job-meta">
                      <Tag tone={recognitionStatus[job.status].tone}>
                        {recognitionStatus[job.status].label}
                      </Tag>
                      <time>{formatDateTime(job.createdAt)}</time>
                    </span>
                    <strong>
                      {job.preview ||
                        (job.imageCount
                          ? `上传了 ${job.imageCount} 张图片`
                          : "提交内容未保留")}
                    </strong>
                    <span className="ai-schedule-job-meta">
                      {job.imageCount > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <ImageIcon size={12} />
                          {job.imageCount} 张图片
                        </span>
                      )}
                      {job.planCount > 0 && <span>{job.planCount} 条日程</span>}
                      {job.error && (
                        <span className="text-destructive">{job.error}</span>
                      )}
                    </span>
                  </span>
                  <ChevronDown
                    size={17}
                    className={`shrink-0 transition-transform ${expanded === job.id ? "rotate-180" : ""}`}
                  />
                </button>
                {expanded === job.id && (
                  <div id={`recognition-${job.id}`}>
                    <JobDetail job={job} />
                  </div>
                )}
              </div>
            ))}
          </div>
          <Pagination
            page={page}
            pages={jobs.data.pagination.totalPages}
            total={jobs.data.pagination.total}
            onChange={(value) => {
              setExpanded(null);
              setPage(value);
            }}
            className="border-t border-sand-light p-5"
          />
        </>
      ) : (
        <EmptyState
          title="没有匹配的识别记录"
          description="调整筛选条件后再试。"
        />
      )}
    </Panel>
  );
}

function JobDetail({ job }: { job: RecognitionJob }) {
  const detail = useResource(
    () => api.recognitionDetail(job.id),
    [job.id, job.updatedAt],
  );
  return (
    <div className="ai-schedule-detail">
      {detail.error && (
        <ErrorState
          error={detail.error}
          onRetry={() => void detail.refresh()}
        />
      )}
      {detail.data ? (
        <RecognitionContent detail={detail.data} />
      ) : detail.loading ? (
        <LoadingState rows={5} />
      ) : null}
    </div>
  );
}

async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("已复制");
  } catch {
    toast.error("复制失败，请手动选择文字复制");
  }
}

export function RecognitionContent({ detail }: { detail: RecognitionDetail }) {
  const [preview, setPreview] = useState<number | null>(null);
  const groups = groupRecognizedPlans(detail.plans);
  const download = () => {
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(detail, null, 2)], { type: "application/json" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `AI日程-${detail.user.account}-${detail.id}.json`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <>
      <div className="ai-schedule-detail-toolbar">
        <span>
          记录 <span className="font-mono">{detail.id.slice(0, 8)}</span> ·
          更新于 {formatDateTime(detail.updatedAt)}
        </span>
        <ActionButton onClick={download}>
          <Download size={13} />
          导出记录
        </ActionButton>
      </div>
      <div className="ai-schedule-comparison">
        <section className="ai-schedule-source">
          <div className="ai-schedule-content-heading">
            <h4>原始提交</h4>
            {detail.input.text && (
              <button
                onClick={() => void copyText(detail.input.text)}
                aria-label="复制原始文字"
              >
                <Copy size={14} />
              </button>
            )}
          </div>
          {detail.input.text ? (
            <p className="ai-schedule-original">{detail.input.text}</p>
          ) : (
            <p className="text-xs text-ink-faint">
              {detail.input.images.length ? "仅提交图片" : "原始内容未保留"}
            </p>
          )}
          {detail.input.images.length > 0 && (
            <div className="ai-schedule-images">
              {detail.input.images.map((image, index) => (
                <SourceImage
                  key={image.url}
                  url={image.url}
                  index={index}
                  onOpen={() => setPreview(index)}
                />
              ))}
            </div>
          )}
          {detail.input.referenceDate && (
            <p className="ai-schedule-reference">
              参考日期 {detail.input.referenceDate}
              {detail.input.timeZone && ` · ${detail.input.timeZone}`}
            </p>
          )}
        </section>
        <section className="ai-schedule-output">
          <div className="ai-schedule-content-heading">
            <h4>
              识别结果{" "}
              <span>
                {detail.plans.length ? `${detail.plans.length} 条日程` : ""}
              </span>
            </h4>
            {detail.plans.length > 0 && (
              <button
                onClick={() => void copyText(recognitionText(detail.plans))}
                aria-label="复制整理结果"
              >
                <Copy size={14} />
              </button>
            )}
          </div>
          {groups.length ? (
            <ol className="space-y-3">
              {groups.map(({ id, plan, items }) => (
                <li className="ai-schedule-plan" key={id}>
                  <strong>{plan.title}</strong>
                  {items.length > 1 ? (
                    <details className="ai-schedule-repeat">
                      <summary>
                        循环日程 · {items.length} 次 <span>查看日期</span>
                      </summary>
                      <ol>
                        {items.map((item) => (
                          <li key={item.id}>{planTime(item)}</li>
                        ))}
                      </ol>
                    </details>
                  ) : (
                    <p className="ai-schedule-plan-time">{planTime(plan)}</p>
                  )}
                  {plan.location && (
                    <p className="ai-schedule-location">
                      <MapPin size={13} />
                      {plan.location}
                    </p>
                  )}
                  {plan.description && (
                    <p className="ai-schedule-description">
                      {plan.description}
                    </p>
                  )}
                </li>
              ))}
            </ol>
          ) : (
            <div className="ai-schedule-result-empty">
              <Sparkles size={24} />
              <strong>
                {detail.status === "failed"
                  ? "未添加日程"
                  : detail.status === "completed"
                    ? "没有日程结果"
                    : "正在整理安排"}
              </strong>
              <p>
                {detail.error ||
                  (detail.status === "queued"
                    ? "任务正在排队"
                    : detail.status === "recognizing"
                      ? "识别完成后显示结果"
                      : "这条记录没有识别结果")}
              </p>
            </div>
          )}
        </section>
      </div>
      {preview !== null && detail.input.images[preview] && (
        <ImagePreview
          images={detail.input.images}
          index={preview}
          onChange={setPreview}
          onClose={() => setPreview(null)}
        />
      )}
    </>
  );
}

function SourceImage({
  url,
  index,
  onOpen,
}: {
  url: string;
  index: number;
  onOpen: () => void;
}) {
  const [failed, setFailed] = useState(false),
    [attempt, setAttempt] = useState(0);
  return failed ? (
    <button
      className="ai-schedule-image-error"
      onClick={() => {
        setFailed(false);
        setAttempt((value) => value + 1);
      }}
    >
      图片 {index + 1}
      <span>重新加载</span>
    </button>
  ) : (
    <button
      className="ai-schedule-image"
      onClick={onOpen}
      aria-label={`预览原始图片 ${index + 1}`}
    >
      <img
        key={attempt}
        src={`${url}?retry=${attempt}`}
        alt={`原始图片 ${index + 1}`}
        loading="lazy"
        onError={() => setFailed(true)}
      />
      <span>{index + 1}</span>
    </button>
  );
}

function ImagePreview({
  images,
  index,
  onChange,
  onClose,
}: {
  images: RecognitionDetail["input"]["images"];
  index: number;
  onChange: (index: number) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    dialog?.showModal();
    return () => dialog?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className="ai-schedule-lightbox"
      aria-label="原始图片预览"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
    >
      <div className="ai-schedule-lightbox-bar">
        <span>
          原始图片 {index + 1} / {images.length}
        </span>
        <button autoFocus onClick={onClose} aria-label="关闭图片预览">
          <X size={22} />
        </button>
      </div>
      <img src={images[index].url} alt={`原始图片 ${index + 1}`} />
      {images.length > 1 && (
        <div className="flex justify-center gap-3 p-4">
          <ActionButton
            disabled={index === 0}
            onClick={() => onChange(index - 1)}
          >
            上一张
          </ActionButton>
          <ActionButton
            disabled={index === images.length - 1}
            onClick={() => onChange(index + 1)}
          >
            下一张
          </ActionButton>
        </div>
      )}
    </dialog>
  );
}
